import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import StudentActivityDialog from "./StudentActivityDialog";
import StudentRow, { TeacherRelationshipRow } from "./StudentRow";
import { useSubscription } from "../../../hooks/useSubscription";
import { useSupportEmail } from "../../../hooks/useSupportEmail";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  changeStudent,
  fetchStudentReport,
  fetchStudents,
  generateJoinLink,
} from "../store/studentsSlice";

import type { StudentRelationship } from "@lingoshade/domain";

export default function StudentsPage(): React.ReactElement {
  const { t } = useTranslation("texts");
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const { teacher, student, link, busy, error } = useAppSelector(
    (state) => state.students,
  );
  const { currentPlan, hasActiveSubscription } = useSubscription();
  const canTeach = hasActiveSubscription && currentPlan?.planType === "Pro";
  const role = params.get("role") === "student" ? "student" : "teacher";
  const navigate = useNavigate();
  const supportEmail = useSupportEmail();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState<StudentRelationship | null>(null);
  const [assignmentsId, setAssignmentsId] = useState<string | null>(null);
  useEffect((): void => {
    void dispatch(fetchStudents());
  }, [dispatch]);
  const current = role === "teacher" ? teacher : student;
  const atLimit = teacher.acceptedCount >= teacher.limit;
  const removalActionKey =
    removing?.status === "ACCEPTED"
      ? "students.confirmRemove"
      : role === "student" && removing?.status === "INVITED"
        ? "students.reject"
        : removing?.status === "REJECTED"
          ? "students.removeFromList"
          : "students.cancelInvitation";
  const update = (
    id: string,
    action: "remove" | "reject" | "request-full" | "allow-full" | "deny-full",
  ): void => {
    void dispatch(changeStudent({ type: "update", id, action }));
  };
  const rows = current.relationships.filter(
    (entry): boolean =>
      ["INVITED", "REQUESTED", "ACCEPTED", "REJECTED"].includes(entry.status) &&
      (role === "student" ||
        entry.email
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase())) &&
      (role === "student" ||
        filter === "ALL" ||
        (filter === "PENDING"
          ? entry.status !== "ACCEPTED"
          : entry.status === "ACCEPTED" &&
            entry.active === (filter === "ACTIVE"))),
  );
  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">
            {t(role === "teacher" ? "students.title" : "students.myTeachers")}
          </Typography>
          {role === "teacher" && (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {t("students.capacity", {
                count: teacher.acceptedCount,
                limit: teacher.limit,
              })}
            </Typography>
          )}
        </Box>
        {role === "teacher" && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ justifyContent: { sm: "flex-end" } }}
          >
            {current.relationships.some(
              (entry): boolean => entry.status !== "REMOVED",
            ) && (
              <>
                <TextField
                  size="small"
                  label={t("students.search")}
                  value={search}
                  onChange={(event): void => setSearch(event.target.value)}
                  sx={{ width: { xs: "calc(50% - 4px)", sm: 200 } }}
                />
                <TextField
                  select
                  size="small"
                  label={t("students.filter")}
                  value={filter}
                  onChange={(event): void => setFilter(event.target.value)}
                  sx={{ width: { xs: "calc(50% - 4px)", sm: 180 } }}
                >
                  {["ALL", "ACTIVE", "INACTIVE", "PENDING"].map(
                    (value): React.ReactElement => (
                      <MenuItem key={value} value={value}>
                        {t(`students.filters.${value}`)}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </>
            )}

            <Button
              variant="contained"
              disabled={busy || !canTeach || atLimit}
              onClick={(): void => setInviteOpen(true)}
            >
              {t("students.invite")}
            </Button>
          </Stack>
        )}
        {role === "student" && (
          <Button variant="outlined" onClick={(): void => setCodeOpen(true)}>
            {t("students.addTeacher")}
          </Button>
        )}
      </Stack>
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {t(`students.errors.${error}`, {
            defaultValue: t("students.errors.STUDENT_TECHNICAL_ERROR"),
          })}
        </Alert>
      )}
      {role === "teacher" ? (
        <>
          {!canTeach && (
            <Alert severity="info">
              {t("students.proRequired")}{" "}
              <Button component={Link} to="/subscription">
                {t("students.viewPro")}
              </Button>
            </Alert>
          )}
          {atLimit && (
            <Alert severity="info" sx={{ my: 2 }}>
              {t("students.limitInfo")}{" "}
              <Button
                href={`mailto:${supportEmail}?subject=${encodeURIComponent(t("students.schoolSubject"))}`}
              >
                {t("students.contactSchools")}
              </Button>
            </Alert>
          )}
        </>
      ) : null}
      {rows.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t(
              role === "teacher" && (filter !== "ALL" || search.trim() !== "")
                ? "students.emptyFilter"
                : role === "teacher"
                  ? "students.emptyStudents"
                  : "students.emptyTeachers",
            )}
          </Typography>
        </Box>
      )}
      {role === "teacher" ? (
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            border: rows.length ? 1 : 0,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          {rows.map(
            (entry): React.ReactElement => (
              <StudentRow
                key={entry.id}
                entry={entry}
                busy={busy}
                canTeach={canTeach}
                atLimit={atLimit}
                assignmentsOpen={assignmentsId === entry.id}
                onAssignments={(): void =>
                  setAssignmentsId(assignmentsId === entry.id ? null : entry.id)
                }
                onActivity={(): void => {
                  void dispatch(fetchStudentReport({ id: entry.id }));
                }}
                onAccept={(): void => {
                  void dispatch(
                    changeStudent({
                      type: "accept",
                      id: entry.id,
                      role: "teacher",
                    }),
                  );
                }}
                onReject={(): void => update(entry.id, "reject")}
                onResend={(): void => {
                  void dispatch(
                    changeStudent({ type: "invite", email: entry.email }),
                  );
                }}
                onRequestFull={(): void => update(entry.id, "request-full")}
                onRemove={(): void => setRemoving(entry)}
              />
            ),
          )}
        </Box>
      ) : (
        <Stack spacing={1}>
          {rows.map(
            (entry): React.ReactElement => (
              <TeacherRelationshipRow
                key={entry.id}
                entry={entry}
                busy={busy}
                onAllowFull={(): void => update(entry.id, "allow-full")}
                onDenyFull={(): void => update(entry.id, "deny-full")}
                onLeave={(): void => setRemoving(entry)}
              />
            ),
          )}
        </Stack>
      )}
      <Dialog
        open={codeOpen}
        onClose={(): void => setCodeOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={(event): void => {
            event.preventDefault();
            setCodeOpen(false);
            navigate(`/teacher-invitations/${encodeURIComponent(code.trim())}`);
          }}
        >
          <DialogTitle>{t("students.addTeacher")}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              required
              size="small"
              label={t("students.enterCode")}
              value={code}
              onChange={(event): void => setCode(event.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={(): void => setCodeOpen(false)}>
              {t("students.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {t("students.reviewTeacher")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={inviteOpen}
        onClose={(): void => setInviteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={(event): void => {
            event.preventDefault();
            void dispatch(changeStudent({ type: "invite", email }))
              .unwrap()
              .then((): void => {
                setInviteOpen(false);
                setEmail("");
              })
              .catch((): void => {});
          }}
        >
          <DialogTitle>{t("students.invite")}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t(`students.errors.${error}`, {
                  defaultValue: t("students.errors.STUDENT_TECHNICAL_ERROR"),
                })}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="email"
              required
              label={t("students.email")}
              value={email}
              onChange={(event): void => setEmail(event.target.value)}
              sx={{ mt: 1 }}
            />
            <Button
              disabled={busy || !canTeach || atLimit}
              sx={{ mt: 2 }}
              onClick={(): void => {
                void dispatch(generateJoinLink());
              }}
            >
              {t(link ? "students.replaceLink" : "students.generateLink")}
            </Button>
            {link && (
              <Box sx={{ mt: 2 }}>
                <Typography>
                  {t("students.joinCode", { code: link.code })}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ overflowWrap: "anywhere", my: 1 }}
                >
                  {link.url}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("students.linkExpires", {
                    date: new Date(link.expiresAt).toLocaleDateString(),
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("students.linkApproval")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("students.linkRotation")}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={(): void => setInviteOpen(false)}>
              {t("students.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !canTeach || atLimit}
            >
              {t("students.invite")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={Boolean(removing)} onClose={(): void => setRemoving(null)}>
        <DialogTitle>
          {t(
            removing?.status === "ACCEPTED"
              ? "students.removeTitle"
              : removalActionKey,
          )}
        </DialogTitle>
        <DialogContent>
          {removing?.status === "ACCEPTED"
            ? t("students.removeTerms")
            : removing?.email}
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => setRemoving(null)}>
            {t("students.cancel")}
          </Button>
          <Button
            color="error"
            onClick={(): void => {
              if (removing)
                update(
                  removing.id,
                  role === "student" && removing.status === "INVITED"
                    ? "reject"
                    : "remove",
                );
              setRemoving(null);
            }}
          >
            {t(removalActionKey)}
          </Button>
        </DialogActions>
      </Dialog>
      <StudentActivityDialog />
    </Box>
  );
}
